import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQCQueriesSlice } from '@restore/qcQueries/qcQueries.slice';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { QueryPage } from 'src/components/Queries/QueryPage/QueryPage';
import {
  QueryEvent,
  QueryStatus,
  QueryTicketDto,
} from '@restore/queries/queries.types';
import { ActionsControl } from 'src/pages/QC/QCQueryPage/ActionsControls';
import { QueryActionModal } from 'src/components/Queries/QueryActionModal';
import { QUERY_MODAL_CONFIG } from '@components/Queries/constants';
import { useAppSelector } from '@hooks/redux';
import { selectAuthView } from '@restore/auth/auth.slice';
import { useQueryCapabilities } from '@components/Queries/flow/useQueryCapabilities';
import { useQCQueryHandlers } from '@components/Queries/useQCQueryHandlers';
import { UserRoles } from '@dynamika-shared/types/auth';

export const QCQueryPage: React.FC = () => {
  const [currentAction, setCurrentAction] = useState<QueryEvent | null>(null);

  const { id: clinicalTrialId, queryId } = useParams<{
    id: string;
    queryId: string;
  }>();

  const [
    loading,
    currentQuery,
    fetchCurrent,
    clearCurrent,
    currentQueryUpdating,
    updateCurrentQuery,
  ] = useQCQueriesSlice((state, actions) => [
    state.currentQueryLoading,
    state.currentQuery as QueryTicketDto,
    actions.fetchCurrentQuery,
    actions.clearCurrentQuery,
    state.currentQueryUpdating,
    actions.updateCurrentQuery,
  ]);

  const { selectedRole } = useAppSelector(selectAuthView);
  const capabilities = useQueryCapabilities(
    currentQuery,
    selectedRole as UserRoles
  );

  const queryLabel = currentQuery?.queryNumber ? `Query ${currentQuery.queryNumber}` : 'Query ...';
  useBreadcrumbs([
    ['QC Queries', 'qcQueries'],
    [queryLabel, 'qcQuery']
  ], [clinicalTrialId!, queryId!]);

  const modalConfig = useMemo(
    () => (currentAction ? QUERY_MODAL_CONFIG[currentAction] : null),
    [currentAction]
  );

  const { handleDetailQuerySubmit } = useQCQueryHandlers({
    ctId: clinicalTrialId!,
    queryId,
    currentAction,
    updating: currentQueryUpdating,
    updateCurrentQuery,
  });

  useEffect(() => {
    if (clinicalTrialId && queryId) {
      fetchCurrent({ queryId, clinicalTrialId });
    }
    return () => {
      clearCurrent();
    };
  }, [clearCurrent, clinicalTrialId, fetchCurrent, queryId]);
  return (
    <>
      <QueryPage
        loading={loading}
        isUpdating={currentQueryUpdating}
        triggerAction={setCurrentAction}
        topRightControls={
          !loading &&
          capabilities.availableEvents.some(cap => !cap.internal) ? (
            <ActionsControl
              capabilities={capabilities}
              onChange={(actionType: QueryEvent) =>
                setCurrentAction(actionType)
              }
            />
          ) : null
        }
        query={currentQuery as QueryTicketDto & { status: QueryStatus }}
      />
      <QueryActionModal
        open={!!currentAction}
        onClose={() => setCurrentAction(null)}
        title={modalConfig?.title}
        okText={modalConfig?.okText}
        textareaLabel={modalConfig?.textareaLabel}
        textareaPlaceholder={modalConfig?.textareaPlaceholder}
        messageRequiredMsg={modalConfig?.messageRequiredMsg}
        enableAttachments={modalConfig?.enableAttachments}
        subtitle={modalConfig?.subtitle}
        onSubmitData={handleDetailQuerySubmit}
        updating={currentQueryUpdating}
        credentialsRequired={currentAction !== QueryEvent.SEND_MESSAGE}
      />
    </>
  );
};
